import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { devicesApi, clientsApi } from "@/lib/apiClient";
import { PageContainer, PageHeader } from "@/components/Layout";
import { Mono, CodePill } from "@/components/Atoms";
import { ArrowLeft, AlertTriangle } from "lucide-react";

export default function DeviceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  // Try GET /api/devices/{id} first. If the backend doesn't expose that
  // endpoint we fall back to the list query and search in-memory.
  const single = useQuery({
    queryKey: ["device", id],
    queryFn: () => devicesApi.get(id),
    retry: 0,
  });
  const list = useQuery({
    queryKey: ["devices", "all"],
    queryFn: () => devicesApi.list(),
    // Only fetch the list as a fallback when the single-fetch errored.
    enabled: !!single.error,
  });

  const clients = useQuery({ queryKey: ["clients"], queryFn: clientsApi.list });
  const types = useQuery({ queryKey: ["device-types"], queryFn: devicesApi.types });

  // Resolve device from either source.
  const device = single.data ?? list.data?.find((x) => x.id === id);

  const fields = useQuery({
    queryKey: ["device-fields", device?.type_id],
    queryFn: () => devicesApi.fields(device.type_id),
    enabled: !!device?.type_id,
    retry: 0,
  });
  const telemetry = useQuery({
    queryKey: ["telemetry", id],
    queryFn: () => devicesApi.telemetry(id, { limit: 40 }),
    enabled: !!id && !!device,
    retry: 0,
  });

  const isLoading = single.isLoading || (single.isError && list.isLoading);
  const notFound = single.isError && list.isSuccess && !device;

  if (isLoading) {
    return (
      <PageContainer>
        <button onClick={() => navigate("/devices")} className="text-xs text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1 mb-3" data-testid="device-back">
          <ArrowLeft className="w-3.5 h-3.5" /> Devices
        </button>
        <p className="text-sm text-zinc-500">Loading device…</p>
      </PageContainer>
    );
  }

  if (notFound || !device) {
    return (
      <PageContainer>
        <button onClick={() => navigate("/devices")} className="text-xs text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1 mb-3" data-testid="device-back">
          <ArrowLeft className="w-3.5 h-3.5" /> Devices
        </button>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-5 max-w-xl" data-testid="device-not-found">
          <div className="flex items-center gap-2 text-amber-700 mb-2">
            <AlertTriangle className="w-4 h-4" />
            <div className="font-heading font-semibold">Device not found</div>
          </div>
          <p className="text-sm text-amber-900">
            No device with id <Mono>{id}</Mono> was returned by the backend.
            This usually means it was deleted, or the endpoint
            <Mono className="mx-1">GET /api/devices/{"{id}"}</Mono> isn&apos;t implemented yet.
          </p>
          {single.error && (
            <p className="mt-2 text-xs text-amber-700 font-mono">{String(single.error.message)}</p>
          )}
        </div>
      </PageContainer>
    );
  }

  const d = device;
  const client = clients.data?.find((c) => c.id === d.client_id);
  const type = types.data?.find((t) => t.id === d.type_id);

  return (
    <PageContainer>
      <button onClick={() => navigate("/devices")} className="text-xs text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1 mb-3" data-testid="device-back">
        <ArrowLeft className="w-3.5 h-3.5" /> Devices
      </button>
      <PageHeader title={d.name} subtitle={`${client?.name || "—"} · ${type?.name || "—"}`} />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Info label="MAC" value={<Mono>{d.mac}</Mono>} />
        <Info label="Protocol" value={<CodePill>{type?.protocol || "—"}</CodePill>} />
        <Info label="Vendor" value={type?.vendor || "—"} />
        <Info label="Registered" value={<span className="font-mono text-xs">{d.created_at ? new Date(d.created_at).toLocaleString() : "—"}</span>} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-zinc-200 rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-zinc-200">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">telemetry · last 40</div>
            <div className="font-heading text-base font-semibold mt-0.5">Recent measurements</div>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-xs uppercase tracking-wider text-zinc-500">
              <tr>
                <th className="text-left p-3 font-semibold">Timestamp</th>
                <th className="text-left p-3 font-semibold">Measurement</th>
                <th className="text-left p-3 font-semibold">Value</th>
                <th className="text-left p-3 font-semibold">Unit</th>
              </tr>
            </thead>
            <tbody>
              {telemetry.data?.length ? telemetry.data.map((t, i) => (
                <tr key={i} className="border-t border-zinc-100">
                  <td className="p-3 text-xs font-mono text-zinc-500">{new Date(t.timestamp).toLocaleString()}</td>
                  <td className="p-3"><CodePill>{t.measurement}</CodePill></td>
                  <td className="p-3 font-mono">{t.value}</td>
                  <td className="p-3 text-zinc-500 font-mono text-xs">{t.unit}</td>
                </tr>
              )) : (
                <tr><td colSpan={4} className="py-8 text-center text-sm text-zinc-500">No telemetry yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-zinc-200">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">device type fields</div>
            <div className="font-heading text-base font-semibold mt-0.5">{type?.name || "—"}</div>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-xs uppercase tracking-wider text-zinc-500">
              <tr>
                <th className="text-left p-3 font-semibold">Name</th>
                <th className="text-left p-3 font-semibold">Type</th>
                <th className="text-left p-3 font-semibold">Unit</th>
              </tr>
            </thead>
            <tbody>
              {fields.data?.length ? fields.data.map((f) => (
                <tr key={f.id} className="border-t border-zinc-100">
                  <td className="p-3 font-medium">{f.name}</td>
                  <td className="p-3"><CodePill>{f.type}</CodePill></td>
                  <td className="p-3 text-zinc-500 font-mono text-xs">{f.unit}</td>
                </tr>
              )) : (
                <tr><td colSpan={3} className="py-6 text-center text-xs text-zinc-500">No fields defined.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </PageContainer>
  );
}

function Info({ label, value }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-lg p-4">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">{label}</div>
      <div className="mt-1.5 text-sm">{value}</div>
    </div>
  );
}
